import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, FileText, Handshake, BarChart3, Calendar, Trophy } from "lucide-react";

export const HowItWorks = () => {
  const steps = [
    {
      number: "1",
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Energy Package Design",
      description: "GreenSmart designs $0-down energy package for school site",
      details: "Solar panels, battery storage, heat pumps, EV chargers"
    },
    {
      number: "2",
      icon: <FileText className="w-6 h-6" />,
      title: "PAC Proposal",
      description: "PAC presents proposal to district with Community Benefit Agreement",
      details: "Comprehensive proposal outlining benefits and compliance"
    },
    {
      number: "3",
      icon: <Handshake className="w-6 h-6" />,
      title: "District Agreement",
      description: "District signs rental/PPA contract and receives rebates/savings",
      details: "Legal framework ensures district compliance and ownership"
    },
    {
      number: "4",
      icon: <BarChart3 className="w-6 h-6" />,
      title: "MOU Execution",
      description: "District and PAC sign MOU: 100% net benefits flow to PAC enrichment fund",
      details: "Formal agreement establishing benefit allocation structure"
    },
    {
      number: "5",
      icon: <Calendar className="w-6 h-6" />,
      title: "Quarterly Reporting",
      description: "District provides quarterly reports; PAC allocates funds to student programs",
      details: "Transparent tracking and allocation of all financial benefits"
    },
    {
      number: "6",
      icon: <Trophy className="w-6 h-6" />,
      title: "Long-term Benefits",
      description: "After 8 years, equipment is paid off — PAC receives full savings indefinitely",
      details: "Permanent funding stream for student enrichment programs"
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 text-green-600 border-green-600">
            PAC-Powered Energy Partnership
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            How the Partnership Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our proven 6-step process ensures PACs can legally access energy upgrade benefits 
            while maintaining full compliance with educational regulations.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <Card key={index} className="relative overflow-hidden group hover:shadow-lg transition-shadow duration-300">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold text-lg">
                    {step.number}
                  </div>
                  <div className="text-green-600">
                    {step.icon}
                  </div>
                </div>
                <CardTitle className="text-lg font-semibold text-gray-900">
                  {step.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-3">
                  {step.description}
                </p>
                <p className="text-sm text-gray-500 italic">
                  {step.details}
                </p>
              </CardContent>
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-bl-full"></div>
            </Card>
          ))}
        </div>

        <div className="mt-16 bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Legal & Operational Framework
            </h3>
            <p className="text-gray-600 mb-6 max-w-4xl mx-auto">
              PACs cannot sign leases or PPAs. GreenSmart solves this via the "PAC-Powered Energy Partnership" model, 
              ensuring full compliance while maximizing benefits for student programs.
            </p>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-3">Sample MOU Clause</h4>
              <p className="text-sm text-gray-600 italic leading-relaxed">
                "The Board of Education agrees that, for the duration of the GreenSmart Energy Services Agreement and thereafter, 
                all net financial benefits derived from the installed energy systems — including utility savings, government rebates, 
                and incentive payments — shall be allocated to the Parent Advisory Council of [School Name] for the exclusive purpose 
                of funding student enrichment programs. This allocation shall be reported publicly and transferred quarterly."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};